import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { ChevronRight, Package, Tv, Wrench, UtensilsCrossed, Store } from 'lucide-react';

const HomePage = () => {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [productWorlds, setProductWorlds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banners, setBanners] = useState([]);
  const [currentBanner, setCurrentBanner] = useState(0);
  const [shopStatus, setShopStatus] = useState(null); // null = unknown, 'none' = keinen Shop, 'exists' = hat Shop

  useEffect(() => {
    loadProductWorlds();
    loadBanners();
    checkShop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const checkShop = async () => {
    const token = localStorage.getItem('token');
    if (!token) { setShopStatus('anonymous'); return; }
    try {
      const res = await api.get('/reseller/shop');
      setShopStatus(res.data?.exists ? 'exists' : 'none');
    } catch {
      setShopStatus('anonymous');
    }
  };

  const loadProductWorlds = async () => {
    try {
      const response = await api.get('/panasonic/worlds');
      setProductWorlds(response.data);
    } catch (error) {
      console.error('Failed to load product worlds', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBanners = async () => {
    try {
      const response = await api.get('/banners');
      if (response.data && response.data.length > 0) {
        setBanners(response.data);
      }
    } catch (error) {
      console.error('Failed to load banners', error);
      // Use fallback banners
      setBanners([
        { text: "🚀 KOSTENLOSER VERSAND ab 150€ Bestellwert", color: "bg-black text-white" }
      ]);
    }
  };

  // Rotate banners only if more than 1
  useEffect(() => {
    if (banners.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 5000); // Rotate every 5 seconds
    return () => clearInterval(interval);
  }, [banners.length]);

  // Icons for product worlds
  const getWorldIcon = (slug) => {
    if (slug.includes('haarschneidemaschinen')) {
      return <Wrench className="w-12 h-12 text-white" />;
    }
    if (slug === 'haushalt' || slug.includes('haushalt')) {
      return <UtensilsCrossed className="w-12 h-12 text-white" />;
    }
    return <Tv className="w-12 h-12 text-white" />;
  };

  // Background images for product worlds
  const getWorldBackground = (slug) => {
    if (slug.includes('haarschneidemaschinen')) {
      return 'https://customer-assets-4nw71qhi.emergentagent.net/job_775d24be-5a96-4a08-95c2-9c74b17b74f4/artifacts/5606fc4m_haarschneidemaschinen.png';
    }
    if (slug === 'haushalt' || slug.includes('haushalt')) {
      return 'https://customer-assets-4nw71qhi.emergentagent.net/job_775d24be-5a96-4a08-95c2-9c74b17b74f4/artifacts/l0d107ti_Haushalt.PNG';
    }
    return 'https://customer-assets-4nw71qhi.emergentagent.net/job_775d24be-5a96-4a08-95c2-9c74b17b74f4/artifacts/89n1pg7a_Unterhaltungselektronik.PNG';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Rotating Promotional Banner */}
      {banners.length > 0 && (
        <div 
          className={`w-full py-2 text-center transition-all duration-500 cursor-pointer ${banners[currentBanner]?.color || 'bg-black text-white'}`}
          onClick={() => banners[currentBanner]?.link && navigate(banners[currentBanner].link)}
          data-testid="promo-banner"
        >
          <p className="text-sm md:text-base font-medium px-4">
            {banners[currentBanner]?.text}
          </p>
          {/* Banner indicators for multiple banners */}
          {banners.length > 1 && (
            <div className="flex justify-center gap-1 mt-1">
              {banners.map((_, idx) => (
                <button
                  key={idx}
                  onClick={(e) => { e.stopPropagation(); setCurrentBanner(idx); }}
                  className={`w-2 h-2 rounded-full transition-all ${idx === currentBanner ? 'bg-white' : 'bg-white/40'}`}
                  aria-label={`Banner ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Onboarding-Banner: Neuer Händler ohne Endkundenshop */}
      {shopStatus === 'none' && (
        <div
          className="bg-gradient-to-r from-amber-100 to-yellow-50 border-b border-amber-300 px-4 py-3"
          data-testid="shop-onboarding-banner"
        >
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-3">
              <Store className="h-6 w-6 text-amber-800 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-900">
                  {t('noShopYet')}
                </p>
                <p className="text-sm text-amber-800">
                  {t('noShopYetText')}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/my-shop')}
              className="bg-amber-800 hover:bg-amber-900 text-white px-4 py-2 rounded font-medium whitespace-nowrap"
              data-testid="onboarding-setup-shop-btn"
            >
              {t('setupShopNow')}
            </button>
          </div>
        </div>
      )}

      {/* Hero Section with Panasonic Image - Mobile Optimized */}
      <section className="relative h-[40vh] sm:h-[50vh] md:h-[60vh] lg:h-[70vh] overflow-hidden">
        {/* Background Image with responsive sizing */}
        <div className="absolute inset-0">
          <picture>
            {/* Mobile: crop to show product better */}
            <source 
              media="(max-width: 640px)" 
              srcSet="https://customer-assets-jt897jd0.emergentagent.net/job_a739737c-f3ba-43c5-afe7-709f286e6c83/artifacts/0xzhtcnf_Panasonic%20ER-GP50.png"
            />
            {/* Desktop: full image */}
            <img 
              src="https://customer-assets-jt897jd0.emergentagent.net/job_a739737c-f3ba-43c5-afe7-709f286e6c83/artifacts/0xzhtcnf_Panasonic%20ER-GP50.png"
              alt="Panasonic ER-GP50 Professional Hair Clipper"
              className="w-full h-full object-cover object-center sm:object-top"
              loading="eager"
            />
          </picture>
          {/* Gradient overlay for mobile text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent sm:from-black/20" />
        </div>
        
        {/* Mobile CTA Button */}
        <div className="absolute bottom-4 left-0 right-0 flex justify-center sm:hidden">
          <button 
            onClick={() => document.getElementById('product-worlds').scrollIntoView({ behavior: 'smooth' })}
            className="bg-white/90 text-black px-6 py-3 rounded-full font-semibold shadow-lg"
          >
            {t('discoverProducts')}
          </button>
        </div>
      </section>

      {/* Info Banner */}
      <div className="w-full flex justify-center py-4 sm:py-6 bg-gray-50">
        <div className="w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-gray-200 rounded-md shadow-sm py-3 text-center">
            <p className="text-black text-xs sm:text-sm md:text-base font-semibold tracking-wide">
              {t('heroBanner')}
            </p>
          </div>
        </div>
      </div>

      {/* Panasonic Product Worlds Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900" data-testid="product-worlds-title">
              {t('productWorlds')}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('chooseWorld')}
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-pulse">
                <div className="h-64 bg-gray-200 rounded-xl max-w-md mx-auto mb-6"></div>
                <div className="h-64 bg-gray-200 rounded-xl max-w-md mx-auto"></div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {productWorlds.map((world) => (
                <div
                  key={world.id}
                  onClick={() => navigate(`/panasonic/${world.slug}`)}
                  className="group relative overflow-hidden rounded-2xl cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1"
                  data-testid={`product-world-card-${world.slug}`}
                >
                  {/* Volles Bild in natürlichem Seitenverhältnis – auf Mobile und Desktop vollständig sichtbar */}
                  <img
                    src={world.image_url || getWorldBackground(world.slug)}
                    alt={world.name}
                    className="w-full h-auto block object-contain transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Panasonic Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-12">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12 text-gray-900">
            {t('whyPanasonic')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-black rounded-full flex items-center justify-center">
                <Package className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-semibold text-lg mb-2">{t('qualityTitle')}</h3>
              <p className="text-gray-600 text-sm">
                {t('qualityText')}
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-black rounded-full flex items-center justify-center">
                <Wrench className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-semibold text-lg mb-2">{t('toolsTitle')}</h3>
              <p className="text-gray-600 text-sm">
                {t('toolsText')}
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-black rounded-full flex items-center justify-center">
                <Tv className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-semibold text-lg mb-2">{t('techTitle')}</h3>
              <p className="text-gray-600 text-sm">
                {t('techText')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full bg-white border-t border-gray-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
          
          <div className="flex justify-end">
            {/* Kontakt */}
            <div className="text-right">
              <h3 className="text-gray-900 font-semibold">{t('contact')}</h3>
              <p className="text-gray-600 text-sm mt-2">Erkan Deniz</p>
              <p className="text-gray-600 text-sm">Mobil: 0171 629 5816</p>
              <p className="text-gray-600 text-sm">E-Mail: ed-steel@outlook.de</p>
            </div>
          </div>

          <div className="mt-10 border-t border-gray-200 pt-6 text-center">
            {/* Footer Links */}
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm font-medium text-gray-600">
              <a href="/impressum" className="hover:text-black hover:underline transition">
                {t('imprint')}
              </a>

              <a href="/datenschutz" className="hover:text-black hover:underline transition">
                {t('privacy')}
              </a>

              <a href="/agb" className="hover:text-black hover:underline transition">
                {t('terms')}
              </a>

              <a href="/widerruf" className="hover:text-black hover:underline transition">
                {t('cancellation')}
              </a>

              <a href="/versand-zahlung" className="hover:text-black hover:underline transition">
                {t('shippingPayment')}
              </a>
            </div>

            {/* Copyright */}
            <p className="mt-4 text-gray-500 text-sm">
              © 2025 ED STEEL - Offizieller Panasonic Partner. {t('allRightsReserved')}
            </p>
          </div>

        </div>
      </footer>
    </div>
  );
};

export default HomePage;
